/**
 * PURE option builder for the unified Line & Area family.
 *
 * No Angular / no ECharts-runtime dependency — takes capabilities + flat rows and
 * returns a plain EChartsOption. All variant / area / stack / dual-axis / series /
 * analytics logic is unit-testable in isolation; the component is a lifecycle
 * shell around `buildLineAreaOption`.
 *
 * Variant → ECharts line-series mapping (flag-driven, see LineAreaVariantFlags):
 *   line/multi-line → plain line(s)              step  → step:'end'
 *   spline          → smooth:true                area  → areaStyle
 *   stacked-area    → area + stack:'total'        area-100 → stacked + normalized
 *   spline-area     → area + smooth
 * A per-measure `axis:'y2'` adds a secondary value axis (dual-Y). A `series` split
 * produces one line/area per distinct value. Analytics (trend / forecast /
 * moving-average) add computed overlay series.
 */
import type { EChartsOption } from 'echarts';
import { toCapitalCase } from '../../utils/text-case.utils';
import type {
  LineAreaCapabilities,
  LineAreaMeasure,
  LineAreaReferenceLine,
} from './unified-line-area-chart.types';

type Row = Record<string, unknown>;
type Agg = NonNullable<LineAreaCapabilities['aggregation']>;

const DEFAULT_PALETTE = ['#1f6feb', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#ec4899', '#84cc16'];

function toNumber(value: unknown): number {
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  if (typeof value === 'string') {
    const cleaned = value.replace(/[^0-9.-]/g, '');
    const n = Number(cleaned);
    return Number.isFinite(n) ? n : 0;
  }
  return 0;
}

function aggregate(values: number[], agg: Agg): number {
  if (!values.length) return 0;
  switch (agg) {
    case 'avg': return values.reduce((a, b) => a + b, 0) / values.length;
    case 'count': return values.length;
    case 'min': return Math.min(...values);
    case 'max': return Math.max(...values);
    case 'sum':
    default: return values.reduce((a, b) => a + b, 0);
  }
}

function distinct(rows: Row[], field: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  rows.forEach((row) => {
    const key = String(row[field] ?? '');
    if (!seen.has(key)) { seen.add(key); out.push(key); }
  });
  return out;
}

function formatValue(value: number, capabilities: LineAreaCapabilities): string {
  if (!Number.isFinite(value)) return '-';
  const scale = capabilities.numberScale || 'none';
  const fmt = capabilities.numberFormat || 'NONE';
  const decimals = Math.max(0, Math.min(6, Math.floor(capabilities.decimals ?? 1)));
  let scaled = value;
  let suffix = '';
  switch (scale) {
    case 'thousands': case 'SCALE_THOUSANDS': scaled = value / 1e3; suffix = 'K'; break;
    case 'millions': case 'MILLIONS': scaled = value / 1e6; suffix = 'M'; break;
    case 'billions': case 'BILLIONS': scaled = value / 1e9; suffix = 'B'; break;
    case 'compact': case 'COMPACT': {
      const abs = Math.abs(value);
      if (abs >= 1e9) { scaled = value / 1e9; suffix = 'B'; }
      else if (abs >= 1e6) { scaled = value / 1e6; suffix = 'M'; }
      else if (abs >= 1e3) { scaled = value / 1e3; suffix = 'K'; }
      break;
    }
    default: break;
  }
  if (fmt === 'PERCENT' || fmt === 'percent') return `${(value * 100).toFixed(decimals)}%`;
  const useCommas = fmt === 'THOUSANDS' || fmt === 'thousands' || suffix === '';
  const body = useCommas && !suffix
    ? scaled.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: decimals })
    : scaled.toFixed(decimals);
  return `${body}${suffix}`;
}

function aggregateByCategory(rows: Row[], category: string, value: string, categories: string[], agg: Agg): number[] {
  const buckets = new Map<string, number[]>();
  rows.forEach((row) => {
    const cat = String(row[category] ?? '');
    const list = buckets.get(cat) || [];
    list.push(toNumber(row[value]));
    buckets.set(cat, list);
  });
  return categories.map((cat) => aggregate(buckets.get(cat) || [], agg));
}

function aggregateByCategoryAndSeries(
  rows: Row[], category: string, value: string, series: string, categories: string[], seriesNames: string[], agg: Agg,
): Map<string, number[]> {
  const grid = new Map<string, Map<string, number[]>>();
  rows.forEach((row) => {
    const s = String(row[series] ?? '');
    const c = String(row[category] ?? '');
    if (!grid.has(s)) grid.set(s, new Map());
    const cats = grid.get(s)!;
    const list = cats.get(c) || [];
    list.push(toNumber(row[value]));
    cats.set(c, list);
  });
  const out = new Map<string, number[]>();
  seriesNames.forEach((s) => {
    const cats = grid.get(s) || new Map<string, number[]>();
    out.set(s, categories.map((c) => aggregate(cats.get(c) || [], agg)));
  });
  return out;
}

/** Least-squares linear fit → returns a fn(x)=>y over the point index. */
function linearFit(values: number[]): (x: number) => number {
  const n = values.length;
  if (n < 2) return () => values[0] ?? 0;
  let sx = 0, sy = 0, sxy = 0, sxx = 0;
  values.forEach((y, x) => { sx += x; sy += y; sxy += x * y; sxx += x * x; });
  const denom = n * sxx - sx * sx || 1;
  const slope = (n * sxy - sx * sy) / denom;
  const intercept = (sy - slope * sx) / n;
  return (x: number) => intercept + slope * x;
}

/** Trailing moving average over a window (nulls before the window fills). */
function movingAverage(values: number[], window: number): Array<number | null> {
  if (window < 2) return values.slice();
  return values.map((_, i) => {
    if (i < window - 1) return null;
    const slice = values.slice(i - window + 1, i + 1);
    return slice.reduce((a, b) => a + b, 0) / slice.length;
  });
}

/** Resolve a config-driven style default (chartDefaults) with a fallback. */
function cd(capabilities: LineAreaCapabilities, key: string, fallback: unknown): unknown {
  const v = (capabilities.chartDefaults || {})[key];
  return v === undefined || v === null ? fallback : v;
}

/**
 * Build the ECharts option for a Line & Area chart from flat rows + capabilities.
 */
export function buildLineAreaOption(rows: Row[], capabilities: LineAreaCapabilities): EChartsOption {
  const safeRows = Array.isArray(rows) ? rows : [];
  const category = capabilities.category;
  const measures = (capabilities.measures || []).filter((m) => m && m.field);
  const agg = capabilities.aggregation || 'sum';

  const palette = (capabilities.colors && capabilities.colors.length ? capabilities.colors
    : capabilities.paletteColors && capabilities.paletteColors.length ? capabilities.paletteColors
      : (cd(capabilities, 'palette', DEFAULT_PALETTE) as string[]));

  // Nothing to plot yet.
  if (!category || !measures.length) {
    return { title: { text: 'Map a category + at least one measure', left: 'center', top: 'middle', textStyle: { color: '#94a3b8', fontSize: 13 } } };
  }

  // ── Flags (config-resolved, name-fallback) ──
  const vf = capabilities.variantFlags || {};
  const isArea = vf.area ?? /area/.test(String(capabilities.variant));
  const isStacked = vf.stack ?? /stacked/.test(String(capabilities.variant));
  const is100 = vf.normalize100 ?? /100/.test(String(capabilities.variant));
  const isStep = vf.step ?? String(capabilities.variant) === 'step';
  const isSmooth = (vf.smooth ?? /spline/.test(String(capabilities.variant))) || capabilities.smoothLines === true;

  const lineWidth = Number(capabilities.lineWidth ?? cd(capabilities, 'lineWidth', 2));
  const symbolSize = Number(capabilities.symbolSize ?? cd(capabilities, 'symbolSize', 0));
  const areaOpacity = Number(capabilities.areaOpacity ?? cd(capabilities, 'areaOpacity', 0.35));
  const connectNulls = capabilities.connectNulls === true;

  // ── Categories (sorted / topN if requested) ──
  let categories = distinct(safeRows, category);
  if (capabilities.sortBy === 'asc' || capabilities.sortBy === 'desc') {
    const firstVal = measures[0].field;
    const totals = aggregateByCategory(safeRows, category, firstVal, categories, agg);
    const pairs = categories.map((c, i) => ({ c, v: totals[i] }));
    pairs.sort((a, b) => capabilities.sortBy === 'asc' ? a.v - b.v : b.v - a.v);
    categories = pairs.map((p) => p.c);
  }
  if (capabilities.topN && capabilities.topN > 0) categories = categories.slice(0, capabilities.topN);

  // ── Build the line/area series ──
  const dualAxis = measures.some((m) => m.axis === 'y2');
  const series: Record<string, unknown>[] = [];
  const legendNames: string[] = [];

  const makeSeries = (name: string, data: Array<number | null>, opts: { color?: string; area?: boolean; axisIndex?: number; stackId?: string }): Record<string, unknown> => {
    const s: Record<string, unknown> = {
      name,
      type: 'line',
      data,
      smooth: isSmooth,
      step: isStep ? 'end' : false,
      symbol: capabilities.symbol || (symbolSize > 0 ? 'circle' : 'none'),
      symbolSize,
      connectNulls,
      lineStyle: { width: lineWidth },
      yAxisIndex: opts.axisIndex ?? 0,
      emphasis: { focus: 'series' },
    };
    if (opts.color) { s['itemStyle'] = { color: opts.color }; s['lineStyle'] = { width: lineWidth, color: opts.color }; }
    if (opts.area) s['areaStyle'] = { opacity: areaOpacity };
    if (opts.stackId) s['stack'] = opts.stackId;
    if (capabilities.showValues) s['label'] = { show: true, formatter: (p: { value: number }) => formatValue(Number(p.value), capabilities) };
    return s;
  };

  if (capabilities.series) {
    // Series split → one line/area per distinct series value (first measure = value).
    const value = measures[0].field;
    const seriesNames = distinct(safeRows, capabilities.series);
    const grid = aggregateByCategoryAndSeries(safeRows, category, value, capabilities.series, categories, seriesNames, agg);
    seriesNames.forEach((sName, i) => {
      let data: Array<number | null> = grid.get(sName) || [];
      if (is100) {
        data = data.map((v, ci) => {
          const total = seriesNames.reduce((sum, sn) => sum + (grid.get(sn)?.[ci] ?? 0), 0);
          return total ? ((v ?? 0) / total) * 100 : 0;
        });
      }
      legendNames.push(sName);
      series.push(makeSeries(sName, data, {
        color: palette[i % palette.length],
        area: isArea,
        stackId: isStacked ? 'total' : undefined,
      }));
    });
  } else {
    // One line/area per measure.
    measures.forEach((m, i) => {
      const name = m.label || toCapitalCase(m.field);
      const data = aggregateByCategory(safeRows, category, m.field, categories, agg);
      const area = (m.renderAs === 'area') || isArea;
      legendNames.push(name);
      series.push(makeSeries(name, data, {
        color: palette[i % palette.length],
        area,
        axisIndex: m.axis === 'y2' ? 1 : 0,
        stackId: isStacked ? 'total' : undefined,
      }));
    });
  }

  // ── Reference / target lines (markLine on the first series) ──
  const refLines = (capabilities.referenceLines || []).filter((r) => r && Number.isFinite(Number(r.value)));
  if (refLines.length && series.length) {
    (series[0] as Record<string, unknown>)['markLine'] = {
      silent: true,
      symbol: 'none',
      data: refLines.map((r: LineAreaReferenceLine) => ({
        yAxis: Number(r.value),
        label: { formatter: r.label || String(r.value), position: 'insideEndTop' },
        lineStyle: { color: r.color || String(cd(capabilities, 'referenceLineColor', '#ef4444')), type: (cd(capabilities, 'referenceLineStyle', 'dashed') as string) },
      })),
    };
  }

  // ── Secondary (top) X axis — a second category column with its own value
  //    series, plotted against xAxisIndex 1. ──
  const secondaryXField = capabilities.secondaryXField;
  let secondaryXCategories: string[] = [];
  if (secondaryXField) {
    secondaryXCategories = distinct(safeRows, secondaryXField);
    const valueField = capabilities.secondaryXValueField || measures[0]?.field || '';
    if (valueField) {
      const data = aggregateByCategory(safeRows, secondaryXField, valueField, secondaryXCategories, agg);
      series.push({
        name: `${toCapitalCase(secondaryXField)} (top)`,
        type: 'line',
        data,
        smooth: isSmooth,
        symbol: symbolSize > 0 ? 'circle' : 'none',
        symbolSize,
        xAxisIndex: 1,
        lineStyle: { width: lineWidth, type: 'dashed', color: palette[(measures.length) % palette.length] },
        itemStyle: { color: palette[(measures.length) % palette.length] },
        emphasis: { focus: 'series' },
      });
      legendNames.push(`${toCapitalCase(secondaryXField)} (top)`);
    }
  }

  // ── Analytics overlays (Trend / Forecast / Moving Average) ──
  const analyticsColor = capabilities.analyticsColor || String(cd(capabilities, 'analyticsColor', '#64748b'));
  const baseData = (series[0]?.['data'] as Array<number | null> | undefined)?.map((v) => Number(v ?? 0)) || [];
  let xLabels = categories.slice();

  if (capabilities.showTrendLine && baseData.length >= 2) {
    const fit = linearFit(baseData);
    series.push({
      name: 'Trend', type: 'line', data: baseData.map((_, x) => Number(fit(x).toFixed(4))),
      smooth: false, symbol: 'none', lineStyle: { width: 2, type: 'dashed', color: analyticsColor }, itemStyle: { color: analyticsColor }, z: 5,
    });
    legendNames.push('Trend');
  }
  // Moving average — only draw it when the window actually FITS the data (a
  // 21-window over 2 points yields all-nulls → an empty ghost line + legend).
  if (capabilities.movingAverageWindow && capabilities.movingAverageWindow > 1 && baseData.length >= capabilities.movingAverageWindow) {
    series.push({
      name: `MA(${capabilities.movingAverageWindow})`, type: 'line',
      data: movingAverage(baseData, capabilities.movingAverageWindow), smooth: true, symbol: 'none', connectNulls: true,
      lineStyle: { width: 2, type: 'dotted', color: analyticsColor }, itemStyle: { color: analyticsColor }, z: 5,
    });
    legendNames.push(`MA(${capabilities.movingAverageWindow})`);
  }
  if (capabilities.forecastPeriods && capabilities.forecastPeriods > 0 && baseData.length >= 2) {
    const n = capabilities.forecastPeriods;
    const fit = linearFit(baseData);
    // Extend the x labels + a forecast series that starts where the data ends.
    const forecastData: Array<number | null> = baseData.map(() => null);
    for (let k = 1; k <= n; k++) {
      xLabels.push(`+${k}`);
      forecastData.push(Number(fit(baseData.length - 1 + k).toFixed(4)));
    }
    // Anchor the forecast to the last real point so the line connects.
    forecastData[baseData.length - 1] = baseData[baseData.length - 1];
    // Pad existing series to the new x length.
    series.forEach((s) => { const d = s['data'] as Array<number | null>; while (d.length < xLabels.length) d.push(null); });
    series.push({
      name: 'Forecast', type: 'line', data: forecastData, smooth: isSmooth, symbol: 'none',
      lineStyle: { width: 2, type: 'dashed', color: analyticsColor }, itemStyle: { color: analyticsColor }, z: 5,
    });
    legendNames.push('Forecast');
  }

  // ── Axes ──
  const showGrid = capabilities.showGridLines !== false;
  const rotation = capabilities.axisLabelRotation;
  const rotate = rotation === 'auto' || rotation === undefined ? undefined : Number(rotation);

  const valueAxisBase = {
    type: (capabilities.valueAxisLog ? 'log' : 'value') as 'value' | 'log',
    splitLine: { show: showGrid },
    min: capabilities.valueAxisMin,
    max: capabilities.valueAxisMax,
    axisLabel: { formatter: (v: number) => formatValue(v, capabilities) },
  };
  const yAxis: Record<string, unknown>[] = [
    { ...valueAxisBase, name: capabilities.yAxisLabel || undefined, nameLocation: 'middle', nameGap: 56 },
  ];
  if (dualAxis) {
    yAxis.push({ ...valueAxisBase, name: capabilities.yAxisLabel2 || undefined, nameLocation: 'middle', nameGap: 56, position: 'right', splitLine: { show: false } });
  }

  const legendPos = capabilities.legendPosition || 'bottom';
  const showLegend = capabilities.showLegend !== false && legendPos !== 'none' && legendNames.length > 1;
  const legend: Record<string, unknown> | undefined = showLegend ? {
    data: legendNames,
    [legendPos === 'top' ? 'top' : legendPos === 'right' ? 'right' : 'bottom']: legendPos === 'right' ? 0 : 8,
    orient: legendPos === 'right' ? 'vertical' : 'horizontal',
  } : undefined;

  const tooltipEnabled = capabilities.tooltipEnabled !== false && capabilities.tooltipTrigger !== 'none';
  const dataZoom = capabilities.enableZoom ? [
    { type: 'inside', xAxisIndex: 0 },
    ...(capabilities.showZoomSlider !== false ? [{ type: 'slider', xAxisIndex: 0, bottom: showLegend ? 28 : 4 }] : []),
  ] : undefined;

  // `containLabel` auto-fits the tick labels but NOT the axis NAMES (titles), so
  // reserve extra room when an X / Y title is set (centered names need the space).
  const hasXTitle = !!(capabilities.xAxisLabel || capabilities.categoryLabel);
  const hasYTitle = !!capabilities.yAxisLabel;
  const hasY2Title = dualAxis && !!capabilities.yAxisLabel2;
  const option: Record<string, unknown> = {
    color: palette,
    grid: {
      left: 48 + (hasYTitle ? 20 : 0),
      right: (dualAxis ? 56 : 24) + (hasY2Title ? 20 : 0),
      top: (legendPos === 'top' ? 40 : 24) + (secondaryXField ? 40 : 0),
      bottom: (showLegend ? 36 : 24) + (dataZoom ? 24 : 0) + (hasXTitle ? 22 : 0),
      containLabel: true,
    },
    tooltip: tooltipEnabled ? {
      trigger: capabilities.tooltipTrigger || 'axis',
      backgroundColor: capabilities.tooltipBackground,
      textStyle: capabilities.tooltipTextColor ? { color: capabilities.tooltipTextColor } : undefined,
      borderColor: capabilities.tooltipBorderColor,
      axisPointer: { type: (capabilities.tooltipAxisPointer as 'line' | 'cross' | 'shadow' | 'none') || 'line' },
    } : { show: false },
    legend,
    xAxis: [
      {
        type: 'category',
        data: xLabels,
        name: capabilities.xAxisLabel || capabilities.categoryLabel || undefined,
        // Centre the axis title UNDER the axis (ECharts defaults it to the far end,
        // which clipped it to a stray "x" on the right).
        nameLocation: 'middle',
        nameGap: 32,
        boundaryGap: isArea ? false : true,
        axisLabel: { rotate, hideOverlap: true },
      },
      // Secondary (top) X axis — only when a secondary X column is mapped.
      ...(secondaryXField ? [{
        type: 'category' as const,
        position: 'top' as const,
        data: secondaryXCategories,
        name: capabilities.xAxisLabel2 || toCapitalCase(secondaryXField),
        nameLocation: 'middle' as const,
        nameGap: 28,
        boundaryGap: true,
        axisLabel: { hideOverlap: true },
      }] : []),
    ],
    yAxis,
    series,
    animation: capabilities.animation !== false,
    dataZoom,
  };

  return option as EChartsOption;
}
